import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  FileDownload as DownloadIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { prefilledDocumentService } from '../services/prefilledDocumentService';
import { IPrefilledDocument } from '../types/prefilledDocument';
import { downloadTextAsPdf } from '../utils/textPdf';

type ViewTab = 'filled' | 'unfilled';

const PrefilledDocumentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [doc, setDoc] = useState<IPrefilledDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState<ViewTab>('filled');

  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editText, setEditText] = useState('');
  const [saving, setSaving] = useState(false);

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const startEditing = useCallback((current: IPrefilledDocument) => {
    setEditTitle(current.title);
    setEditText(current.filledText);
    setTab('filled');
    setIsEditing(true);
  }, []);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    prefilledDocumentService
      .getById(id)
      .then((data) => {
        if (cancelled) return;
        setDoc(data);
        if (new URLSearchParams(location.search).get('edit') === 'true') {
          startEditing(data);
        }
      })
      .catch((err: any) => {
        if (!cancelled) setError(err.response?.data?.error || 'Failed to load the document.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, location.search, startEditing]);

  const handleSave = async () => {
    if (!doc) return;
    setSaving(true);
    setError('');
    try {
      const updated = await prefilledDocumentService.update(doc.id, {
        title: editTitle.trim() || doc.title,
        filledText: editText,
      });
      setDoc(updated);
      setIsEditing(false);
      navigate(`/prefilled-documents/${doc.id}`, { replace: true });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    if (doc) navigate(`/prefilled-documents/${doc.id}`, { replace: true });
  };

  const handleDownload = async () => {
    if (!doc) return;
    const text = tab === 'filled' ? doc.filledText : doc.unfilledText;
    const title = tab === 'filled' ? doc.title : `${doc.title} (unfilled)`;
    try {
      await downloadTextAsPdf(title, text);
    } catch {
      setError('Failed to download the document.');
    }
  };

  const handleDelete = async () => {
    if (!doc) return;
    setDeleting(true);
    try {
      await prefilledDocumentService.remove(doc.id);
      navigate('/prefilled-documents');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete the document.');
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!doc) {
    return (
      <Box sx={{ p: 3, maxWidth: 900, mx: 'auto' }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error || 'Document not found.'}</Alert>
        <Button variant="outlined" startIcon={<BackIcon />} onClick={() => navigate('/prefilled-documents')}>
          Back to Pre-filled Documents
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          {isEditing ? (
            <TextField
              label="Title"
              size="small"
              fullWidth
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              sx={{ maxWidth: 500 }}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Typography variant="h4" sx={{ fontWeight: 700 }}>{doc.title}</Typography>
              <Chip
                size="small"
                label={doc.unansweredCount === 0 ? 'All filled' : `${doc.unansweredCount} left blank`}
                color={doc.unansweredCount === 0 ? 'success' : 'warning'}
                variant="outlined"
              />
            </Box>
          )}
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
            Filled from: {doc.sourceDocumentTitle} &bull; Uploaded file: {doc.originalFilename} &bull; Updated:{' '}
            {new Date(doc.updatedAt).toLocaleDateString()}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="outlined" size="small" startIcon={<BackIcon />} onClick={() => navigate('/prefilled-documents')}>
            Back
          </Button>
          {isEditing ? (
            <>
              <Button variant="outlined" size="small" color="inherit" disabled={saving} onClick={handleCancelEdit}>
                Cancel
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
                disabled={saving || !editText.trim()}
                onClick={handleSave}
              >
                Save
              </Button>
            </>
          ) : (
            <>
              <Button variant="contained" size="small" color="secondary" startIcon={<EditIcon />} onClick={() => startEditing(doc)}>
                Edit
              </Button>
              <Button variant="contained" size="small" startIcon={<DownloadIcon />} onClick={handleDownload}>
                Download PDF
              </Button>
              <Button variant="outlined" size="small" color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmDelete(true)}>
                Delete
              </Button>
            </>
          )}
        </Box>
      </Box>

      {!isEditing && (
        <Tabs value={tab} onChange={(_, value: ViewTab) => setTab(value)} sx={{ mb: 2 }}>
          <Tab value="filled" label="Filled" />
          <Tab value="unfilled" label="Unfilled" />
        </Tabs>
      )}

      <Paper elevation={1} sx={{ p: { xs: 3, md: 5 }, borderRadius: 2, backgroundColor: '#FFFFFF', minHeight: 500 }}>
        {isEditing ? (
          <TextField
            fullWidth
            multiline
            minRows={18}
            maxRows={50}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            helperText="Replace any ___ with the correct answer."
          />
        ) : (
          <Box data-testid="prefilled-text" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, fontSize: '1.05rem', color: 'text.primary' }}>
            {tab === 'filled' ? doc.filledText : doc.unfilledText}
          </Box>
        )}
      </Paper>

      <Dialog open={confirmDelete} onClose={() => !deleting && setConfirmDelete(false)}>
        <DialogTitle>Delete pre-filled document?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">"{doc.title}" will be permanently deleted. This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} disabled={deleting} color="inherit">Cancel</Button>
          <Button onClick={handleDelete} disabled={deleting} color="error" variant="contained">
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PrefilledDocumentDetail;
